import type { AxiosInstance } from 'axios';
import type {
  CommonListArgs,
  Country,
  GetEntityByIdParams,
  IsoDate,
  Nullable,
  PaginatedList,
  PartnerShort,
  SortListArgs,
} from './types.ts';
import { GameStatusResult } from '../assets/enums/game-status-result.enum.ts';
import { prepareOrderingForBackend } from '../assets/helpers/utils/prepare-ordering-for-backend.ts';

export class PlayersApi {
  constructor(private instance: AxiosInstance) {
  }

  public async getPlayers(params: PlayersListArgs) {
    return this.instance.get<PaginatedList<PlayerListItem>>('/users/players/', {
      params: {
        page: params.page,
        search: params.search,
        ordering: prepareOrderingForBackend(params.sortBy, params.sortDirection),
        region_id: params.region_id,
      },
    }).then(res => res.data);
  };

  public async getPlayerById(params: GetEntityByIdParams) {
    return this.instance.get<PlayerDetail>(`/users/players/${params.id}/`).then(res => res.data);
  };

  public async getPlayerGamesHistory(params: PlayerGameHistoryListArgs) {
    return this.instance.get<PaginatedList<PlayerGameHistoryItem>>(`/users/players/${params.id}/games-history/`, { params }).then(res => res.data);
  };

}

export type PlayersListArgs = {
  search?: string;
  region_id?: string;
} & CommonListArgs & SortListArgs;

export type PlayerGamesPerDayItem = {
  date: IsoDate
  games_count: number
}
export type PlayerGamesPerDay = {
  from_date: IsoDate
  to_date: IsoDate
  total_games: number
  max_games_per_day: number
  min_games_per_day: number
  series: PlayerGamesPerDayItem[]
}

export type FavouriteCourse = {
  id: string
  name: string
  country: Country
  partner: PartnerShort
  games_count: number
}

export type PlayerDetail = {
  id: string
  username: string
  photo: Nullable<string>
  partner: Nullable<PartnerShort>
  personal_payout_multiplier: number
  phone: string
  country: Nullable<Country>
  won_amount: number
  lost_amount: number
  games_played: number
  win_rate: number
  member_since: IsoDate
  last_activity: IsoDate
  max_won_amount: number
  max_lost_amount: number
  favourite_course: Nullable<FavouriteCourse>
  games_per_day: PlayerGamesPerDay
}

export type PlayerListItem = {
  id: string
  username: string
  photo: Nullable<string>
  partner: Nullable<PartnerShort>
  personal_payout_multiplier: number
  phone: string
  country: Nullable<Country>
  won_amount: string
  lost_amount: string
  games_played: number
  win_rate: number
  member_since: IsoDate
  last_activity: IsoDate
}
export type PlayerGameHistoryListArgs = CommonListArgs & GetEntityByIdParams;
export type PlayerGameHistoryItem = {
  id: number
  partner: Nullable<PartnerShort>
  game_type: Nullable<string>
  wager_amount: number
  prize: number
  date_time: IsoDate
  status: GameStatusResult
}